import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileText, Search, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { openPdf } from "@/components/ImpicPeriodo";
import { KycArchiveLog, ExportAllButton } from "@/components/KycArchiveLog";

const TONE = { validada: "bg-emerald-50 text-emerald-700 border-emerald-200", preenchida: "bg-amber-50 text-amber-800 border-amber-200",
  na_direcao: "bg-sky-50 text-sky-800 border-sky-200", correcao: "bg-rose-50 text-rose-700 border-rose-200" };

export async function downloadZip(body, onDone) {
  try {
    const res = await api.post("/kyc-archive/export", body, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
    a.download = `Arquivo_KYC_${new Date().toISOString().slice(0, 10)}.zip`;
    a.click();
    toast.success("Exportação descarregada");
  } catch (e) {
    const txt = e.response?.data instanceof Blob ? await e.response.data.text() : null;
    toast.error((txt && JSON.parse(txt).detail) || "Não foi possível exportar.");
  }
  onDone?.();
}

function Row({ r, onPdf }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`kyc-archive-row-${r.id}`}>
      <td className="py-3 px-3"><div className="font-medium text-ec-green">{r.name}</div><div className="text-xs text-slate-500">{r.role}</div></td>
      <td className="py-3 px-3 tabular-nums" data-testid={`kyc-archive-nif-${r.id}`}>{r.nif || "—"}</td>
      <td className="py-3 px-3">
        {r.process_id ? <Link to={`/processo/${r.process_id}`} className="font-medium hover:underline">{r.ref}</Link> : <span className="font-medium">{r.ref}</span>}
        <div className={`text-xs ${r.process === "Processo apagado" ? "text-rose-600" : "text-slate-500"}`}>{r.process}</div>
      </td>
      <td className="py-3 px-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${TONE[r.status]}`} data-testid={`kyc-archive-status-${r.id}`}>{r.status_label}</span>
        {r.validated_by && <div className="text-xs text-slate-500 mt-1">por {r.validated_by}</div>}</td>
      <td className="py-3 px-3 whitespace-nowrap tabular-nums">{toPt((r.date || "").slice(0, 10))}</td>
      <td className="py-3 px-3 text-sm" data-testid={`kyc-archive-keep-${r.id}`}>{r.keep_until ? <span className="tabular-nums whitespace-nowrap">{toPt(r.keep_until.slice(0, 10))}</span> : <span className="text-slate-500">7 anos após o fim do processo</span>}</td>
      <td className="py-3 px-3 text-right">
        {r.has_pdf ? <button onClick={() => onPdf(r)} className="inline-flex items-center gap-1 text-sm font-semibold text-ec-green hover:underline" data-testid={`kyc-archive-pdf-${r.id}`}><FileText size={14} /> PDF</button> : <span className="text-xs text-slate-400">—</span>}
      </td>
    </tr>
  );
}

function MobileCard({ r, onPdf }) {
  return (
    <div className="rounded-xl border border-slate-100 p-3.5" data-testid={`kyc-archive-card-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0"><div className="font-medium text-ec-green">{r.name}</div><div className="text-xs text-slate-500">{r.role} · NIF {r.nif || "—"}</div></div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${TONE[r.status]}`}>{r.status_label}</span>
      </div>
      <div className="text-sm mt-2">{r.process_id ? <Link to={`/processo/${r.process_id}`} className="font-medium hover:underline">{r.ref}</Link> : <span className="font-medium">{r.ref}</span>} <span className="text-slate-500">· {r.process}</span></div>
      <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
        <span>Ficha {toPt((r.date || "").slice(0, 10))} · guardar até {r.keep_until ? toPt(r.keep_until.slice(0, 10)) : "7 anos após o fim"}</span>
        {r.has_pdf && <button onClick={() => onPdf(r)} className="inline-flex items-center gap-1 text-sm font-semibold text-ec-green" data-testid={`kyc-archive-card-pdf-${r.id}`}><FileText size={14} /> PDF</button>}
      </div>
    </div>
  );
}

const toIso = (s) => { const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/); return m ? `${m[3]}-${m[2]}-${m[1]}` : ""; };
const toPt = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

function DateField({ value, onChange, testid }) {
  const [t, setT] = useState(toPt(value));
  const change = (e) => {
    const v = e.target.value.replace(/[^\d/]/g, "").slice(0, 10);
    setT(v);
    if (!v || toIso(v)) onChange({ target: { value: toIso(v) } });
  };
  return <input value={t} onChange={change} placeholder="dd/mm/aaaa" inputMode="numeric" className="block mt-1 w-36 rounded-lg border border-slate-200 px-3 py-2 bg-white tabular-nums" data-testid={testid} />;
}

function Filters({ f, setF }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="flex flex-col md:flex-row gap-3 md:items-end">
      <label className="flex-1 relative">
        <span className="text-xs text-slate-500">Pesquisar</span>
        <Search size={15} className="absolute left-3 bottom-3 text-slate-400" />
        <input value={f.q} onChange={set("q")} placeholder="Nome, NIF ou n.º do processo" className="w-full mt-1 rounded-lg border border-slate-200 pl-9 pr-3 py-2.5 bg-white" data-testid="kyc-archive-search" />
      </label>
      <label><span className="text-xs text-slate-500">Desde</span><DateField value={f.date_from} onChange={set("date_from")} testid="kyc-archive-from" /></label>
      <label><span className="text-xs text-slate-500">Até</span><DateField value={f.date_to} onChange={set("date_to")} testid="kyc-archive-to" /></label>
    </div>
  );
}

export default function ArquivoKyc() {
  const [f, setF] = useState({ q: "", date_from: "", date_to: "" });
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/kyc-archive", { params: f }).then(({ data }) => setD(data)).catch(() => toast.error("Não foi possível carregar o arquivo."));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  const onPdf = async (r) => { if (await openPdf(`/kyc-archive/${r.id}/pdf`, `Ficha_KYC_${r.ref}.pdf`)) load(); };
  const exp = (all) => { setBusy(true); downloadZip({ ...f, all }, () => { setBusy(false); load(); }); };
  const n = d?.items.length || 0;
  return (
    <div className="space-y-6" data-testid="kyc-archive-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl text-ec-green flex items-center gap-3"><ShieldCheck size={30} strokeWidth={1.6} /> Arquivo KYC</h1>
        <p className="text-slate-500 mt-1 max-w-3xl">Todas as fichas de identificação guardadas, incluindo as de processos fechados ou apagados. A Lei 83/2017 obriga a guardá-las 7 anos após o fim da relação de negócio. Só a direção vê esta página.</p>
      </div>
      <div className="bg-white rounded-[16px] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-4 md:p-5 space-y-4">
        <Filters f={f} setF={setF} />
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <p className="text-sm text-slate-600" data-testid="kyc-archive-count">{d ? `${n} de ${d.total} fichas` : "A carregar…"}</p>
          <div className="flex flex-wrap gap-2">
            <ExportAllButton total={d?.total || 0} disabled={busy || !d?.total} onConfirm={() => exp(true)} />
            <button onClick={() => exp(false)} disabled={busy || !n} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-ec-green hover:brightness-110 disabled:opacity-50 transition-[filter]" data-testid="kyc-archive-export">
              <Download size={15} /> {busy ? "A preparar…" : `Exportar (${n})`}
            </button>
          </div>
        </div>
        <div className="md:hidden space-y-2" data-testid="kyc-archive-cards">
          {d && !n && <p className="py-6 text-center text-slate-400">Nenhuma ficha corresponde à pesquisa.</p>}
          {d?.items.map((r) => <MobileCard key={r.id} r={r} onPdf={onPdf} />)}
        </div>
        <div className="hidden md:block overflow-x-auto -mx-4 md:-mx-5">
          <table className="w-full min-w-[900px] text-sm" data-testid="kyc-archive-table">
            <thead><tr className="text-left text-xs text-slate-500">
              {["Pessoa", "NIF", "Processo", "Estado", "Data da ficha", "Guardar até", ""].map((h) => <th key={h} className="py-2.5 px-3 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {d && !n && <tr><td colSpan={7} className="py-10 text-center text-slate-400" data-testid="kyc-archive-empty">Nenhuma ficha corresponde à pesquisa.</td></tr>}
              {d?.items.map((r) => <Row key={r.id} r={r} onPdf={onPdf} />)}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400">«Exportar» descarrega um ZIP com os PDFs e uma folha CSV das fichas da pesquisa atual. Cada exportação e cada PDF aberto ficam registados abaixo.</p>
      </div>
      <KycArchiveLog log={d?.log || []} />
    </div>
  );
}
